import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import EditBusiness from "./forms/EditBusiness";
import UploadLogo from "./forms/UploadLogo";
import { useBusiness, usePermaLink, updatePermaLink } from "@/_actions/businessActions";
import { useProfile } from "@/_actions/userActions";
import Notification from "@/components/overlays/Notification";
import { usePlan } from "@/_actions/authActions";
import {
  CheckIcon,
  ExternalLinkIcon,
  InformationCircleIcon,
  LinkIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import { buildPortalUrl } from "@/utils/buildPortalUrl";
import { handleApiError } from "@/utils/errorHandler";

export default function BusinessProfile() {
  const { data: profile, mutate: mutateProfile } = useProfile();
  const { data = {}, mutate: mutateBusiness } = useBusiness(profile?.client);
  const [edit, toggleEdit] = useState(false);
  const [showNotification, toggleNotification] = useState(false);
  const [textNotification, setTextNotification] = useState("");
  const { data: planData } = usePlan();
  const { plan } = planData || {};
  const [showBanner, setShowBanner] = useState(false);
  useEffect(() => {
    if (data?.business_name == "" || profile?.logo_client == "") {
      setShowBanner(true);
      toggleEdit(true);
    } else {
      setShowBanner(false);
    }
  }, [profile, data]);

  const role = profile?.role;

  const handleToggleNotification = (text) => {
    setTextNotification(text);
    toggleNotification(true);
    setTimeout(() => {
      toggleNotification(false);
    }, 5000);
  };

  return (
    <div className="sm:p-2 lg:p-4">
      {showBanner && (
        <div className="flex w-full rounded-lg border border-[#3655E0] bg-[#E7EBFA] p-2 font-bold text-[#0C2078]">
          <InformationCircleIcon className="mr-1 w-5" />
          Completa los datos👇 para publicar propiedades y comienza a recibir clientes potenciales
          👥 🎯
        </div>
      )}
      <UploadLogo
        logoClient={profile?.logo_client}
        callBack={() => {
          mutateProfile();
          mutateBusiness();
          handleToggleNotification("Cambios realizados con éxito");
        }}
        clientId={profile?.client}
      />
      <p className="mt-1 block text-xl font-medium leading-tight text-black">
        Datos de la inmobilaria
      </p>
      <p className="mt-2 text-gray-500">
        Estos datos aparecerán en la guía inmobiliaria y en caso de que falten datos de contacto en
        alguna propiedad.
      </p>
      {!edit ? (
        <>
          <div className="mt-3 text-sm font-semibold uppercase tracking-wide text-blue-800">
            {data.business_name}
          </div>
          <div className="mt-3 flex">
            <svg
              className="h-6 w-6 shrink-0 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>

            <span className="ml-3">{data?.whatsapp || "-"}</span>
          </div>
          <div className="mt-3 flex">
            <svg
              className="h-6 w-6 shrink-0 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span className="ml-3">{data?.email_contact || "-"}</span>
          </div>
          {["admin", "owner"].includes(role) ? (
            <button
              onClick={() => toggleEdit(true)}
              type="button"
              data-splitbee-event="account-edit-contact"
              className="button-secondary mt-5"
            >
              <span className="inline">Editar </span>
              <svg
                className=" ml-2 -mr-0.5 inline h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          ) : null}

          {["admin", "owner"].includes(role) && plan?.plan_addons?.cyt_landing ? (
            <div className="mt-7">
              <PermaLinkForm
                callBack={() => handleToggleNotification("Url actualizada con éxito")}
              />
            </div>
          ) : null}
        </>
      ) : (
        <EditBusiness
          data={data}
          callBack={() => {
            toggleEdit(false);
            mutateBusiness();
            handleToggleNotification("Cambios realizados con éxito");
          }}
          firstLogin={showBanner}
          cancel={() => toggleEdit(false)}
        />
      )}
      <Notification open={showNotification} setOpen={toggleNotification} title={textNotification} />
    </div>
  );
}

function PermaLinkForm({ callBack }) {
  const { data, isLoading, error, mutate } = usePermaLink();
  const [editor, setEditor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const saveInFlight = useRef(false);
  const savedPermalink = typeof data?.permalink === "string" ? data.permalink : "";
  const isEditing = editor !== null;
  const hasChanges = isEditing && editor.value !== editor.initialValue;
  const canSave = hasChanges && Boolean(editor.value.trim()) && !isSaving;
  const savedUrl = savedPermalink.trim()
    ? buildPortalUrl(`/inmobiliaria/${encodeURIComponent(savedPermalink)}`)
    : "";
  const urlPrefix = buildPortalUrl("/inmobiliaria/");
  const canVisit = Boolean(savedUrl) && !hasChanges && !isSaving;

  const startEditing = () => {
    setEditor({ value: savedPermalink, initialValue: savedPermalink });
    setSaveError("");
  };

  const cancelEditing = () => {
    setEditor(null);
    setSaveError("");
  };

  const copyPublishedUrl = async () => {
    if (!canVisit) return;

    try {
      await navigator.clipboard.writeText(savedUrl);
      toast.success("URL copiada al portapapeles");
    } catch {
      toast.error("No pudimos copiar la URL. Puedes seleccionarla y copiarla manualmente.");
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canSave || saveInFlight.current) return;

    saveInFlight.current = true;
    setIsSaving(true);
    setSaveError("");
    try {
      const confirmed = await updatePermaLink({ permalink: editor.value });
      if (typeof confirmed?.permalink !== "string" || !confirmed.permalink.trim()) {
        setSaveError("No se pudo confirmar la URL guardada. Intenta de nuevo.");
        return;
      }
      await mutate(confirmed, false);
      setEditor(null);
      callBack();
    } catch (err) {
      const fieldError = err.response?.data?.permalink;
      setSaveError(
        Array.isArray(fieldError)
          ? fieldError.join(" ")
          : typeof fieldError === "string"
            ? fieldError
            : handleApiError(err).userMessage,
      );
    } finally {
      saveInFlight.current = false;
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <p
        role="status"
        className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-500"
      >
        Cargando URL de la inmobiliaria…
      </p>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p role="alert" className="text-sm text-red-700">
          No pudimos cargar la URL de tu inmobiliaria. Intenta de nuevo.
        </p>
        <button
          type="button"
          className="button-secondary mt-2"
          onClick={() => mutate().catch(() => {})}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <section
      aria-labelledby="business-page-title"
      className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 sm:p-5"
    >
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          aria-label="Copiar URL de la inmobiliaria"
          title="Copiar URL de la inmobiliaria"
          onClick={copyPublishedUrl}
          disabled={!canVisit}
          className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-blue-50 text-primary-default transition-colors hover:bg-blue-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
        >
          <LinkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <h2 id="business-page-title" className="text-sm font-semibold text-gray-900">
            Página de tu inmobiliaria
          </h2>
          <p className="mt-0.5 text-xs leading-5 text-gray-500">
            Comparte esta dirección para que visiten tu inmobiliaria.
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        {isEditing ? (
          <form onSubmit={onSubmit} aria-busy={isSaving} className="min-w-0 flex-1">
            <label htmlFor="business-permalink" className="block text-xs font-medium text-gray-600">
              URL de la inmobiliaria
            </label>
            <div className="mt-2 flex min-w-0 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-primary-default focus-within:ring-2 focus-within:ring-blue-100 sm:flex-row">
              <span className="flex items-center break-all border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500 sm:shrink-0 sm:border-r sm:border-b-0 sm:text-sm">
                {urlPrefix}
              </span>
              <input
                type="text"
                name="permalink"
                id="business-permalink"
                value={editor.value}
                className="block min-w-0 w-full border-0 bg-white px-3 py-2.5 text-sm text-gray-900 focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500"
                disabled={isSaving}
                required
                aria-invalid={Boolean(saveError)}
                aria-describedby={`business-permalink-help${saveError ? " business-permalink-error" : ""}`}
                onChange={(event) => {
                  setEditor({ ...editor, value: event.target.value });
                  setSaveError("");
                }}
              />
            </div>
            <p id="business-permalink-help" className="mt-2 text-xs leading-5 text-gray-500">
              {isSaving
                ? "Guardando tu nueva URL…"
                : hasChanges
                  ? "Guarda los cambios para visitar la nueva URL."
                  : "Edita el nombre al final de la dirección para personalizar tu URL."}
            </p>
            {saveError && (
              <p id="business-permalink-error" role="alert" className="mt-2 text-sm text-red-700">
                {saveError}
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="submit"
                className="button-primary inline-flex min-h-10 items-center justify-center gap-2 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
                disabled={!canSave}
              >
                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                {isSaving ? "Guardando…" : "Guardar"}
              </button>
              <button
                type="button"
                className="inline-flex min-h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSaving}
                onClick={cancelEditing}
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : (
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-500">URL publicada</p>
            <p className="mt-1.5 break-all text-sm leading-6">
              {savedUrl ? (
                <>
                  <span className="text-gray-500">{urlPrefix}</span>
                  <span className="font-medium text-primary-default">
                    {savedUrl.slice(urlPrefix.length)}
                  </span>
                </>
              ) : (
                <span className="text-gray-500">Sin URL configurada</span>
              )}
            </p>
          </div>
        )}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!isEditing && (
            <button
              type="button"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
              onClick={startEditing}
            >
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
              Editar URL
            </button>
          )}
          {canVisit ? (
            <a
              className={`${isEditing ? "button-secondary" : "button-primary"} inline-flex min-h-10 items-center justify-center gap-2 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default`}
              rel="noreferrer"
              target="_blank"
              href={savedUrl}
            >
              Ver página
              <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" />
            </a>
          ) : (
            <button
              type="button"
              className="button-secondary inline-flex min-h-10 items-center justify-center gap-2 text-sm font-medium"
              disabled
            >
              Ver página
              <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
